import '@/assets/assistant-ui.css';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRightIcon, MessageSquare } from 'lucide-react';
import { useAppContext } from '@/Main';
import { cn } from '@/lib/utils';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { useListMcpChatSuggestionsQuery } from '../api/mcpApi';
import { SkillInputFlow } from '../components/skill-flow/SkillInputFlow';
import { useOptionalMcpChatOnboarding } from '../hooks/McpChatOnboardingContext';
import { buildSuggestionUserMessage } from '../utils/suggestionMarkers';
import {
  collectRawValues,
  fillPromptTemplate,
  suggestionInputsToSteps,
  type SuggestionDomainOption
} from '../utils/suggestionCollect';

/**
 * Standalone page for a "collect"-mode FAQ: walks through its input steps,
 * then starts a fresh chat with the filled prompt (same as running a skill).
 */
export default function FaqStepsPage() {
  const { key = '' } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const onboarding = useOptionalMcpChatOnboarding();
  const {
    config: { isDark }
  } = useAppContext();

  const { data, isLoading, isError } = useListMcpChatSuggestionsQuery();
  const { data: userInfo } = useGetUserInfoQuery();

  const item = useMemo(
    () => data?.questions?.find(question => question.key === key) ?? null,
    [data, key]
  );
  const domains = useMemo<SuggestionDomainOption[]>(() => {
    const list = userInfo?.data?.domains;
    return Array.isArray(list) ? (list as SuggestionDomainOption[]) : [];
  }, [userInfo]);
  const steps = useMemo(
    () => (item?.inputs?.length ? suggestionInputsToSteps(item.inputs) : []),
    [item]
  );

  const backToChat = () => navigate('/chat');

  const onComplete = (answers: Record<string, string[]>) => {
    if (!item) return;
    if (onboarding && !onboarding.assertChatAllowed()) return;
    const inputs = item.inputs ?? [];
    const prompt = fillPromptTemplate(item.prompt, inputs, answers, domains);
    const collectValues = collectRawValues(inputs, answers, domains);
    navigate('/chat', {
      replace: true,
      state: {
        mcpNewChat: Date.now(),
        initialPrompt: buildSuggestionUserMessage(
          prompt,
          item.key,
          collectValues
        )
      }
    });
  };

  let body;
  if (isLoading) {
    body = (
      <p className="text-muted-foreground m-0 py-10 text-center text-sm">
        در حال بارگذاری…
      </p>
    );
  } else if (isError || !item || steps.length === 0) {
    body = (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <MessageSquare
          className="text-muted-foreground size-6"
          strokeWidth={1.75}
          aria-hidden
        />
        <p className="m-0 text-sm">این سوال پیدا نشد.</p>
        <button
          type="button"
          className="mcp-chat-pill-btn mcp-chat-pill-btn--primary"
          onClick={backToChat}
        >
          بازگشت به گفتگو
        </button>
      </div>
    );
  } else {
    body = (
      <SkillInputFlow
        id={`suggestion-${item.id}`}
        title={item.label}
        steps={steps}
        domainSelectClassName="position-relative w-full max-w-xs p-0"
        onComplete={onComplete}
      />
    );
  }

  return (
    <div
      className={cn('mcp-chat-page aui-root', isDark && 'dark')}
      data-theme={isDark ? 'dark' : 'light'}
    >
      <div className="mcp-chat-main" dir="rtl">
        <header className="mcp-chat-header">
          <div className="d-flex align-items-center gap-2 min-w-0">
            <button
              type="button"
              className="mcp-chat-pill-btn mcp-chat-pill-btn--ghost mcp-chat-pill-btn--sm"
              onClick={backToChat}
            >
              <ChevronRightIcon className="size-3.5" aria-hidden />
              بازگشت
            </button>
            <h1 className="mcp-chat-header-heading">سوالات متداول</h1>
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[50rem] px-4 py-6 sm:px-6">
            {body}
          </div>
        </div>
      </div>
    </div>
  );
}
